import { Routes } from '@Components/main-app/types'
import { AuthScopes, FeatureFlag, PlayerWithPAMDetails } from '@Generated/back-office-client/src'
import { Button, Container, Flex, Heading, Skeleton, TabNav, Text } from '@radix-ui/themes'
import { useEffect, useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { WithPermissions } from '@Components/common/with-permissions.tsx'
import { PlayerServiceAPI } from '@API/data-services/player-service'
import { usePlayerCapabilities } from './use-player-capabilities'

const getNavRoute = (route: string, playerDetails: PlayerWithPAMDetails) => {
  return route.replace(':lookupId', playerDetails.player.lookupId)
}

enum NavTabs {
  DETAILS = 'details',
  SCANS = 'scans',
  COUPONS = 'coupons',
  SPINS = 'spins',
  REWARDS = 'rewards',
}

export const PlayerDetailsRoot = () => {
  const location = useLocation()
  const params = useParams()
  const nav = useNavigate()
  const subpath = location.pathname.split('/').filter((x) => x)
  const currentPath = subpath[subpath.length - 1]
  const defaultNav = subpath.length === 2 ? NavTabs.DETAILS : (currentPath as NavTabs) // if the current path is just details, it doesn't have the trailing part in the url (length 2) otherwise map to the enum
  const [selectedNavItem, setSelectedNavItem] = useState<NavTabs>(defaultNav)

  const playerLookupId = params?.lookupId
  const locationState = location.state as
    | (PlayerWithPAMDetails & { from?: string })
    | { from?: string }
    | null
  const statePlayerDetails =
    playerLookupId && locationState && 'player' in locationState ? locationState : null
  const backTo =
    locationState && 'from' in locationState && locationState.from
      ? locationState.from
      : Routes.PLAYER_SEARCH
  const [playerDetails, setPlayerDetails] = useState<PlayerWithPAMDetails | null>(
    statePlayerDetails,
  )
  const [isLoading, setIsLoading] = useState(false)
  const hasFetchedRef = useRef(false)
  const { data: capabilities } = usePlayerCapabilities()
  const isExternallyManaged = capabilities !== undefined && !capabilities.isEditable

  useEffect(() => {
    // If we have player details from location state, use those
    if (statePlayerDetails) {
      setPlayerDetails(statePlayerDetails)
      hasFetchedRef.current = false // Reset in case we navigate to a different player
      return
    }

    // If no state but we have a lookupId, fetch from API
    // This allows direct URL navigation (e.g., bookmarked pages, manual URL edits)
    if (playerLookupId && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      setIsLoading(true)
      PlayerServiceAPI.getPlayerByLookup(playerLookupId)
        .then((details) => {
          setPlayerDetails(details)
        })
        .catch(() => {
          // If fetch fails, redirect to search
          nav(Routes.PLAYER_SEARCH)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [playerLookupId, statePlayerDetails, nav])

  if (isLoading) {
    return (
      <Container className="player-data-container">
        <Skeleton height="200px" />
      </Container>
    )
  }

  if (!playerDetails) {
    return null
  }

  return (
    <Container className="player-data-container">
      <Flex direction="column" gap="3">
        <Flex justify="between">
          <Heading as="h1" size="5">
            {[playerDetails.details.firstName, playerDetails.details.lastName]
              .filter((part) => Boolean(part))
              .join(' ') || 'Player Name Not Available'}
          </Heading>
          <Button asChild>
            <Link to={backTo}>Back</Link>
          </Button>
        </Flex>
        <Flex justify="between">
          <Text>Player ID: {playerDetails.player.lookupId}</Text>
          {selectedNavItem === NavTabs.DETAILS && isExternallyManaged ? (
            <Text size="2">
              <InfoCircledIcon />
              &nbsp;This information is managed externally
            </Text>
          ) : null}
        </Flex>
      </Flex>

      <TabNav.Root className="player-data-nav">
        <WithPermissions requiredPermissions={[AuthScopes.readplayer_details]}>
          <TabNav.Link
            active={selectedNavItem === NavTabs.DETAILS}
            onClick={() => {
              setSelectedNavItem(NavTabs.DETAILS)
              nav(getNavRoute(Routes.PLAYER_DETAILS, playerDetails), {
                state: { ...playerDetails, from: backTo },
                replace: true,
              })
            }}>
            Player Details
          </TabNav.Link>
        </WithPermissions>
        <WithPermissions requiredPermissions={[AuthScopes.readplayer_scans]}>
          <TabNav.Link
            active={selectedNavItem === NavTabs.SCANS}
            onClick={() => {
              setSelectedNavItem(NavTabs.SCANS)
              nav(getNavRoute(Routes.PLAYER_DETAILS_SCANS, playerDetails), {
                state: { ...playerDetails, from: backTo },
                replace: true,
              })
            }}>
            Ticket Scanner
          </TabNav.Link>
        </WithPermissions>
        <WithPermissions
          requiredPermissions={[AuthScopes.readplayer_coupons]}
          requiredFeature={FeatureFlag.coupons}>
          <TabNav.Link
            active={selectedNavItem === NavTabs.COUPONS}
            onClick={() => {
              setSelectedNavItem(NavTabs.COUPONS)
              nav(getNavRoute(Routes.PLAYER_DETAILS_COUPONS, playerDetails), {
                state: { ...playerDetails, from: backTo },
                replace: true,
              })
            }}>
            Coupons
          </TabNav.Link>
        </WithPermissions>
        <WithPermissions
          requiredPermissions={[AuthScopes.readplayer_spins]}
          requiredFeature={FeatureFlag.spin_to_win}>
          <TabNav.Link
            active={selectedNavItem === NavTabs.SPINS}
            onClick={() => {
              setSelectedNavItem(NavTabs.SPINS)
              nav(getNavRoute(Routes.PLAYER_DETAILS_SPINS, playerDetails), {
                state: { ...playerDetails, from: backTo },
                replace: true,
              })
            }}>
            Spin to Win
          </TabNav.Link>
        </WithPermissions>
        <WithPermissions requiredPermissions={[AuthScopes.readplayer_rewards]}>
          <TabNav.Link
            active={selectedNavItem === NavTabs.REWARDS}
            onClick={() => {
              setSelectedNavItem(NavTabs.REWARDS)
              nav(getNavRoute(Routes.PLAYER_DETAILS_REWARDS, playerDetails), {
                state: { ...playerDetails, from: backTo },
                replace: true,
              })
            }}>
            Rewards
          </TabNav.Link>
        </WithPermissions>
      </TabNav.Root>

      <Outlet context={{ playerDetails, onDetailsUpdated: setPlayerDetails }} />
    </Container>
  )
}
